import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLiffSession } from '../hooks/useLiff';

/**
 * mesoestetic LINE Login LP
 *
 * — Light theme (white bg + black text) per medical-device CI
 * — Reads `utm_content` from URL → renders campaign-specific copy (message-match)
 * — Whitelist guard: unknown utm_content falls back to default (XSS-safe)
 *
 * Variants whitelisted: ebook · consult · lucky · coupon400
 * Anything else → default copy
 */

interface Copy {
  eyebrow: string;
  title: string;
  em: string;
  sub: string;
  cta: string;
}

const VARIANTS: Record<string, Copy> = {
  ebook: {
    eyebrow: 'ดาวน์โหลดฟรี E-book',
    title:   'เผยความลับฝ้า',
    em:      'ดูแลอย่างไรให้ฝ้าหาย',
    sub:     'ความยาว 16 หน้า ฉบับสมบูรณ์\nส่งฟรีทาง LINE',
    cta:     'รับคู่มือผ่าน LINE',
  },
  consult: {
    eyebrow: 'Skin Advisor · Free',
    title:   'ปรึกษาปัญหาฝ้า',
    em:      'กับ Skin Advisor',
    sub:     'ส่งรูปหน้า ดูเคสคุณก่อนตัดสินใจ\nใช้เวลา 15 นาที · ไม่มีค่าใช้จ่าย',
    cta:     'เริ่มปรึกษาผ่าน LINE',
  },
  lucky: {
    eyebrow: 'ลงทะเบียนลุ้นรับฟรี · cosmelan',
    title:   'ลุ้นรับ cosmelan 2 ฟรี',
    em:      'รวมมูลค่า ฿14,000',
    sub:     '2 รางวัล รางวัลละ 1 กระปุก · จับทุกวันที่ 5\nประกาศผลผ่านทาง Line Timeline',
    cta:     'ลงทะเบียนลุ้นรางวัล',
  },
  coupon400: {
    eyebrow: 'รับคูปองลดราคาพิเศษ',
    title:   'คูปอง ฿400',
    em:      'เริ่มต้น Cosmelan',
    sub:     'ช๊อป mesoestetic ในราคาถูกที่สุด\nซื้อตรงผ่านทาง Line CRM',
    cta:     'รับคูปองเลย',
  },
};

const DEFAULT_COPY: Copy = {
  eyebrow: 'ระบบสมาชิกของ mesoestetic Thailand',
  title:   'mesoestetic Official',
  em:      'Reward',
  sub:     'เพิ่ม LINE เพื่อรับราคาที่ถูกที่สุด\nสะสมคะแนน · สิทธิ์ประโยชน์สมาชิก · คูปองส่วนลด',
  cta:     'เริ่มต้นด้วย LINE',
};

export function Welcome() {
  const { login } = useLiffSession();
  const [searchParams] = useSearchParams();

  // ── Whitelist guard — unknown key falls back to DEFAULT_COPY (XSS-safe by design)
  const copy = useMemo<Copy>(() => {
    const key = searchParams.get('utm_content') ?? '';
    return VARIANTS[key] ?? DEFAULT_COPY;
  }, [searchParams]);

  return (
    <div className="min-h-screen min-h-[100dvh] bg-white text-[#111111] font-sans">
      <div className="max-w-[440px] mx-auto min-h-screen min-h-[100dvh]
                      flex flex-col justify-center text-center px-6 py-12">
        <div className="w-full">

          {/* Brand wordmark */}
          <div className="leading-none">
            <span className="text-[32px] font-medium tracking-[-0.02em] text-[#111111]">
              mesoestetic
            </span>
            <sup className="text-[12px] align-[16px] opacity-55 font-normal">®</sup>
          </div>

          {/* Hero */}
          <div className="pt-12 pb-8">
            <div className="text-[11px] font-medium tracking-[0.22em] text-[#6B7280] uppercase mb-[18px]">
              {copy.eyebrow}
            </div>
            <h1 className="text-[26px] font-semibold leading-[1.3] tracking-[-0.01em] text-[#111111]">
              {copy.title}<br />
              <span className="font-semibold text-[#111111]">{copy.em}</span>
            </h1>
            <div className="w-7 h-px bg-[#C7C7C7] mx-auto mt-[18px]" />
            <p className="text-[14px] font-normal text-[#525252] mt-[18px] leading-[1.7] whitespace-pre-line">
              {copy.sub}
            </p>
          </div>

          {/* CTA — keep LINE green for brand recognition */}
          <button
            onClick={login}
            className="w-full bg-[#06C755] text-white border-none rounded-xl py-[15px] px-5
                       text-[15px] font-semibold flex items-center justify-center gap-[10px]
                       cursor-pointer transition-all duration-150 hover:opacity-90 active:scale-[0.99]"
            style={{ boxShadow: '0 1px 3px rgba(6, 199, 85, 0.2)' }}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M12 2C6.486 2 2 5.682 2 10.211c0 4.06 3.557 7.466 8.359 8.108.327.071.766.215.879.495.099.255.064.652.031.91l-.143.857c-.044.255-.205.998.875.544 1.081-.453 5.831-3.434 7.957-5.881 1.466-1.609 2.171-3.245 2.171-5.033C22 5.682 17.514 2 12 2zM7.95 13.247a.196.196 0 0 1-.197.196H5.345a.196.196 0 0 1-.197-.196V8.875c0-.108.088-.196.197-.196H6.04c.109 0 .196.088.196.196v3.582h1.518c.108 0 .196.088.196.196v.594zm1.683 0a.197.197 0 0 1-.197.196h-.694a.197.197 0 0 1-.197-.196V8.875c0-.108.088-.196.197-.196h.694c.108 0 .197.088.197.196v4.372zm4.847 0a.197.197 0 0 1-.197.196h-.737a.205.205 0 0 1-.157-.077l-2.005-2.706v2.587a.197.197 0 0 1-.196.196h-.695a.197.197 0 0 1-.197-.196V8.875c0-.108.088-.196.197-.196h.738a.198.198 0 0 1 .157.078l1.998 2.696V8.875c0-.108.088-.196.196-.196h.695c.108 0 .197.088.197.196v4.372zm3.918-3.78a.197.197 0 0 1-.197.196h-1.519v.586h1.519c.108 0 .197.088.197.196v.594a.197.197 0 0 1-.197.197h-1.519v.587h1.519c.108 0 .197.088.197.196v.594a.197.197 0 0 1-.197.196h-2.408a.197.197 0 0 1-.196-.196V8.875c0-.108.088-.196.196-.196h2.408c.108 0 .197.088.197.196v.592z"/>
            </svg>
            <span>{copy.cta}</span>
          </button>

          {/* Trust row */}
          <div className="flex justify-center gap-5 mt-4">
            <span className="inline-flex items-center gap-[5px] text-[11px] font-normal text-[#6B7280]">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                   strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 2l8 3v6c0 5-3.5 9-8 11-4.5-2-8-6-8-11V5l8-3z"/>
                <path d="M9 12l2 2 4-4"/>
              </svg>
              ข้อมูลปลอดภัย PDPA
            </span>
            <span className="inline-flex items-center gap-[5px] text-[11px] font-normal text-[#6B7280]">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                   strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="10"/>
                <path d="M2 12h20M12 2a15.3 15.3 0 010 20M12 2a15.3 15.3 0 000 20"/>
              </svg>
              110 ประเทศทั่วโลก
            </span>
          </div>

          {/* Footer */}
          <p className="text-[11px] font-normal text-[#9CA3AF] mt-8 leading-[1.6]">
            การเข้าสู่ระบบถือว่ายอมรับ<br />
            <a
              href="https://mesoestetic-th.com/policies/privacy-policy"
              target="_blank" rel="noopener noreferrer"
              className="text-[#6B7280] underline underline-offset-2"
            >
              เงื่อนไขการใช้งาน
            </a>
          </p>

        </div>
      </div>
    </div>
  );
}
